import React, { useCallback, useEffect, useState } from "react";

import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWatch } from "antd/es/form/Form";
import { FormItemProps } from "antd/es/form/FormItem";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import isMailtoURI from "validator/es/lib/isMailtoURI";
import isURL from "validator/es/lib/isURL";

import { parseUrl } from "@mapmycustomers/shared/util/url";

import Button from "../../../Button";
import { useConfigProvider } from "../../../ConfigProvider";
import FormItem from "../../../FormItem";
import UrlField from "../../../input/UrlField";

import styles from "./UrlFormItem.module.scss";

type UrlFormItemProps = Pick<FormItemProps, "required">;

const ALLOWED_SCHEMES = ["https", "mailto", "ftp"];

const UrlFormItem = ({ required }: UrlFormItemProps) => {
  const configProvider = useConfigProvider();

  const form = useFormInstance();
  const url = useWatch("url", form);

  const [urlInvalid, setUrlInvalid] = useState(false);

  useEffect(() => {
    // form validation is async, so we need to wait for the next tick to get the updated errors
    setTimeout(() => {
      setUrlInvalid(form.getFieldError("url").length > 0);
    });
  }, [form, url]);

  const handlePreviewClick = useCallback(() => {
    window.open(url, "_blank");
  }, [url]);

  return (
    <div className={styles.container}>
      <FormItem
        name="url"
        required={required}
        rules={[
          {
            message: configProvider.formatMessage(
              "ui.emailBody.urlFormItem.validation.urlRequired"
            ),
            required: true,
          },
          () => ({
            validator(_, value) {
              if (!value) {
                return Promise.resolve();
              }

              const url = parseUrl(value);

              if (
                !url ||
                (url.protocol === "mailto:"
                  ? !isMailtoURI(value)
                  : !isURL(value, { protocols: ALLOWED_SCHEMES, require_protocol: true }))
              ) {
                return Promise.reject(
                  configProvider.formatMessage("ui.emailBody.urlFormItem.validation.urlInvalid")
                );
              }

              return Promise.resolve();
            },
          }),
        ]}
      >
        <UrlField
          allowedSchemes={ALLOWED_SCHEMES}
          label={configProvider.formatMessage("ui.emailBody.urlFormItem.url")}
          placeholder={configProvider.formatMessage("ui.emailBody.urlFormItem.url")}
          required={required}
        />
      </FormItem>
      <Button
        className={styles.previewButton}
        disabled={!url || urlInvalid}
        onClick={handlePreviewClick}
        rightIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
        type="link"
      >
        {configProvider.formatMessage("ui.emailBody.urlFormItem.preview")}
      </Button>
    </div>
  );
};

export default UrlFormItem;
